/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useMemo, useState} from "react";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import { GreenLineDataSetsData } from "./GreenLineReportWizard";
import { GreenLineDataSetStates } from "../../../SensingSystemArchitectureDesign/types";
import {getUrlForDataset, renderLink} from "../../../CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/DrivingScenarioProperties";
import {ArgosTableSimple} from "../../../../components/Table/ArgosTableSimple";
import ArgosButton from "../../../../components/Buttons/ArgosButton";

interface SelectGreenLineDataSetProps {
  dataSets: GreenLineDataSetsData[]
  selectedDataSets: MRT_RowSelectionState
  setSelectedDataSets: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>
}

/**
 * This wizard step allows user to select the green line data set to use when
 * creating a report.
 */
export const SelectGreenLineDataSet = (props: SelectGreenLineDataSetProps) => {
  const { dataSets, selectedDataSets, setSelectedDataSets } = props

  const greenLineDataSetTableColumns = useMemo<MRT_ColumnDef<GreenLineDataSetsData>[]>(
    () => [
      {
        id: "study",
        header: "Study",
        accessorKey: 'studyName'
      },
      {
        id: "greenLineDataSet",
        header: "Green Line Data Set",
        accessorKey: 'greenLineDataSetName',
      },
      {
        accessorKey: "state",
        header: "State",
      },
      {
        accessorKey: "sensorLocation",
        header: "Sensor Location",
      },
      {
        accessorKey: "sensorModality",
        header: "Sensor Modality",
      },
      {
        accessorKey: "sensorName",
        header: "Sensor",
      },
      {
        header: "Red Line Data Set",
        accessorKey: 'redLineDataSetIdToData',
        Cell: ({cell}) => {
          const redLineDataSetIdToData = cell.row.original.redLineDataSetIdToData
          const datasetIds = redLineDataSetIdToData ? Object.keys(redLineDataSetIdToData) : []
          if (datasetIds.length === 0) {
            return <div>None Selected</div>
          }
          return (
            <ArgosFlexbox>
              {datasetIds.map((datasetId: string) => {
                const datasetName = redLineDataSetIdToData[datasetId]?.name ?? ''
                const studyId = redLineDataSetIdToData[datasetId]?.studyId ?? ''
                const urlForDataset = getUrlForDataset(1, studyId, datasetId)
                return (
                  <div key={datasetId}>
                    {renderLink(datasetName, urlForDataset)}
                  </div>
                )
              })}
            </ArgosFlexbox>
          )
        }
      },
    ],
    [],
  )

  const [isShowingOnlyPor, setIsShowingOnlyPor] = useState(true);
  const porMsg = isShowingOnlyPor ? 'Show all (not just Plan of Record) green lines' : 'Show only Plan of Record green lines';
  const actions = (
    <div>
      <ArgosButton onClick={()=>setIsShowingOnlyPor(prevState => !prevState)}>{porMsg}</ArgosButton>
    </div>
  );

  const filteredDataSets = useMemo(() => {
    return dataSets.filter((dataSet) => {
      if (isShowingOnlyPor) {
        return dataSet.isStudyPor;
      }
      return true;
    })
  }, [dataSets, isShowingOnlyPor]);

  // Ensure selected datasets are visible by adjusting the POR filter on initial mount or when selection changes
  useEffect(() => {
    const selectedDataSetIds = Object.keys(selectedDataSets);
    if (selectedDataSetIds.length === 0) return;

    const allDataSetIds = dataSets.map(ds => ds.greenLineDataSetId);
    const porDataSetIds = dataSets.filter(ds => ds.isStudyPor).map(ds => ds.greenLineDataSetId);

    if (isShowingOnlyPor) {
      if (!selectedDataSetIds.every(id => porDataSetIds.includes(id))) {
        setIsShowingOnlyPor(false);
      }
    } else {
      if (!selectedDataSetIds.every(id => allDataSetIds.includes(id))) {
        setIsShowingOnlyPor(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Suppressing react-hooks/exhaustive-deps because the dependency array is intentionally limited to avoid unnecessary re-renders.
  }, [selectedDataSets, dataSets]);

  // Reset selection if it becomes invisible after filter toggle
  useEffect(() => {
    const selectedDataSetIds = Object.keys(selectedDataSets);
    const filteredDataSetIds = filteredDataSets.map((dataSet) => dataSet.greenLineDataSetId);

    if (selectedDataSetIds.some(id => !filteredDataSetIds.includes(id))) {
      setSelectedDataSets({});
    }
  }, [filteredDataSets, selectedDataSets, setSelectedDataSets]);

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%'}}>
      <ArgosTableSimple
        data={filteredDataSets}
        columns={greenLineDataSetTableColumns}
        layoutMode='semantic'
        getRowId={(row) => row.greenLineDataSetId}
        state={{rowSelection: selectedDataSets}}
        enablePagination={false}
        enableRowSelection={(row) => row.original.state === GreenLineDataSetStates.COMPLETE}
        enableMultiRowSelection={false}
        onRowSelectionChange={setSelectedDataSets}
        renderTopToolbarCustomActions={() => actions}
      />
    </ArgosFlexbox>
  )
}